import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import './SendNotification.css';
import api from '../../api/axios';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: '🌐 All Users' },
  { value: 'vendors', label: '🏪 All Vendors' },
  { value: 'customers', label: '👤 All Customers' },
  { value: 'category', label: '🏷️ Specific Category' },
  { value: 'user', label: '🎯 Specific User' },
];

const STATUS_BADGE = {
  processing: { label: 'Processing', className: 'badge-processing' },
  completed: { label: 'Completed', className: 'badge-completed' },
  failed: { label: 'Failed', className: 'badge-failed' },
};

const SendNotification = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audienceType, setAudienceType] = useState('all');

  const [categories, setCategories] = useState([]);
  const [categoryType, setCategoryType] = useState('');

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const userSearchTimer = useRef(null);

  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const limit = 10;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchHistory(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (audienceType !== 'user') return;
    if (!userSearch.trim()) {
      setUserResults([]);
      return;
    }
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(() => searchUsers(userSearch), 350);
    return () => clearTimeout(userSearchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, audienceType]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/admin/notifications/categories');
      setCategories(res.data?.data || []);
    } catch {
      toast.error('Failed to load categories');
    }
  };

  const searchUsers = async (term) => {
    try {
      setUserSearchLoading(true);
      const res = await api.get('/admin/all-users', { params: { search: term, limit: 8 } });
      setUserResults(res.data?.data || []);
    } catch {
      toast.error('Failed to search users');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const fetchHistory = async (pageNo) => {
    try {
      setHistoryLoading(true);
      const res = await api.get('/admin/notifications/broadcast', {
        params: { page: pageNo, limit },
      });
      if (res.data.success) {
        setHistory(res.data.items || []);
        setPagination(res.data.pagination || {});
      }
    } catch {
      toast.error('Failed to load notification history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setAudienceType('all');
    setCategoryType('');
    setUserSearch('');
    setUserResults([]);
    setSelectedUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (audienceType === 'category' && !categoryType) {
      toast.error('Please select a category');
      return;
    }
    if (audienceType === 'user' && !selectedUser) {
      toast.error('Please select a user');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        audienceType,
        ...(audienceType === 'category' ? { categoryType } : {}),
        ...(audienceType === 'user' ? { targetUserId: selectedUser._id } : {}),
      };
      const res = await api.post('/admin/notifications/broadcast', payload);

      if (res.data?.data?.totalRecipients === 0) {
        toast.warning('No matching users found for this audience');
      } else {
        toast.success(
          `Notification queued for ${res.data?.data?.totalRecipients ?? 0} user(s)`
        );
      }

      resetForm();
      setPage(1);
      fetchHistory(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const audienceLabel = (item) => {
    if (item.audienceType === 'category') {
      return `Category: ${item.categoryName || '—'}`;
    }
    if (item.audienceType === 'user') {
      return `User: ${item.targetUserName || '—'}`;
    }
    return AUDIENCE_OPTIONS.find((o) => o.value === item.audienceType)?.label || item.audienceType;
  };

  return (
    <div className="send-notification">
      <div className="send-notification-header">
        <h2>Push Notifications</h2>
        <p>Compose a notification and send it to all users, all vendors, all customers, vendors in a specific category, or one particular user.</p>
      </div>

      <div className="send-notification-content">
        <div className="compose-section">
          <h3>Compose Notification</h3>
          <form onSubmit={handleSubmit} className="notification-form">
            <div className="input-group">
              <label htmlFor="title">Title</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. New Year Offer!"
                maxLength={100}
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write the notification message..."
                maxLength={500}
                rows={4}
                required
              />
              <span className="char-count">{message.length} / 500</span>
            </div>

            <div className="input-group">
              <label>Audience</label>
              <div className="audience-options">
                {AUDIENCE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`audience-chip ${audienceType === opt.value ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="audienceType"
                      value={opt.value}
                      checked={audienceType === opt.value}
                      onChange={(e) => {
                        setAudienceType(e.target.value);
                        setCategoryType('');
                        setSelectedUser(null);
                        setUserSearch('');
                        setUserResults([]);
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {audienceType === 'category' && (
              <div className="input-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={categoryType}
                  onChange={(e) => setCategoryType(e.target.value)}
                  required
                >
                  <option value="">
                    {categories.length === 0 ? 'No categories found' : 'Select category'}
                  </option>
                  {categories.map((c) => (
                    <option key={c.type} value={c.type}>
                      {c.title} ({c.vendorCount} vendor{c.vendorCount !== 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
                <p className="field-note">Sent to vendors who have listed a service in this category.</p>
              </div>
            )}

            {audienceType === 'user' && (
              <div className="input-group">
                <label htmlFor="userSearch">Search User</label>
                {selectedUser ? (
                  <div className="selected-user-chip">
                    <span>
                      <strong>{selectedUser.name || 'Unnamed'}</strong>
                      {' — '}
                      {selectedUser.mobile_number || selectedUser.email || selectedUser.userType}
                    </span>
                    <button
                      type="button"
                      className="clear-user-btn"
                      onClick={() => { setSelectedUser(null); setUserSearch(''); }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      id="userSearch"
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name, mobile number or email"
                    />
                    {userSearchLoading && <p className="field-note">Searching...</p>}
                    {userResults.length > 0 && (
                      <div className="user-search-results">
                        {userResults.map((u) => (
                          <div
                            key={u._id}
                            className="user-search-result-item"
                            onClick={() => { setSelectedUser(u); setUserResults([]); }}
                          >
                            <strong>{u.name || 'Unnamed'}</strong>
                            <span>{u.mobile_number || u.email || '—'} · {u.userType}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <button type="submit" className="send-btn" disabled={sending}>
              {sending ? 'Sending...' : '📤 Send Notification'}
            </button>
          </form>
        </div>

        <div className="preview-section">
          <h3>Preview</h3>
          <div className="notification-preview-card">
            <div className="preview-icon">🔔</div>
            <div className="preview-body">
              <div className="preview-title">{title || 'Notification title'}</div>
              <div className="preview-message">{message || 'Your message will appear here.'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="notification-history-section">
        <h3>Notification History</h3>
        <div className="table-container">
          <table className="notification-history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Message</th>
                <th>Audience</th>
                <th>Recipients</th>
                <th>Push Sent</th>
                <th>Status</th>
                <th>Sent By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr><td colSpan="9" align="center">Loading...</td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan="9" align="center">No notifications sent yet</td></tr>
              ) : (
                history.map((item, index) => (
                  <tr key={item._id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td className="col-title">{item.title}</td>
                    <td className="col-message" title={item.message}>{item.message}</td>
                    <td>{audienceLabel(item)}</td>
                    <td>{item.totalRecipients}</td>
                    <td>{item.pushSuccessCount}/{item.pushSuccessCount + item.pushFailureCount}</td>
                    <td>
                      <span className={`status-badge ${STATUS_BADGE[item.status]?.className || ''}`}>
                        {STATUS_BADGE[item.status]?.label || item.status}
                      </span>
                    </td>
                    <td>{item.sentBy?.name || '-'}</td>
                    <td>{formatDateTime(item.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>{page} / {pagination.totalPages || 1}</span>
          <button
            disabled={page === pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >Next</button>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default SendNotification;
